/**
 * Instrumentación del Píxel de Meta (ID 1093714805571053, cargado en index.html).
 *
 * Todas las llamadas fallan en silencio: si el píxel no está disponible
 * (bloqueador de anuncios, red, etc.) la landing sigue funcionando igual y
 * los enlaces navegan normal.
 */

type FbqFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

/** Eventos estándar de Meta: se envían con `track`, el resto con `trackCustom`. */
const STANDARD_EVENTS = new Set<string>(["FindLocation"]);

/** Ventana para descartar dobles disparos del mismo clic. */
const DEDUPE_WINDOW_MS = 400;

function getFbq(): FbqFn | null {
  if (typeof window === "undefined") return null;
  return typeof window.fbq === "function" ? window.fbq : null;
}

export function trackMetaEvent(
  name: string,
  params: Record<string, string> = {},
): void {
  const fbq = getFbq();
  if (!fbq) return;

  try {
    if (STANDARD_EVENTS.has(name)) {
      fbq("track", name, params);
    } else {
      fbq("trackCustom", name, params);
    }
  } catch {
    // Falla en silencio: la navegación del clic no se ve afectada.
  }
}

/**
 * Delegación de eventos sobre `document`.
 * Lee `data-fb-event` y `data-fb-label` desde el elemento clicado (o su
 * ancestro más cercano), de modo que un mismo clic nunca dispara dos eventos.
 */
export function initMetaPixelTracking(): () => void {
  if (typeof document === "undefined") return () => undefined;

  let lastKey = "";
  let lastAt = 0;

  const handleClick = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest<HTMLElement>("[data-fb-event]");
    if (!trigger) return;

    const name = (trigger.dataset.fbEvent ?? "").trim();
    if (!name) return;

    const label = (trigger.dataset.fbLabel ?? "").trim();
    const now = Date.now();
    const key = `${name}::${label}`;

    if (key === lastKey && now - lastAt < DEDUPE_WINDOW_MS) return;
    lastKey = key;
    lastAt = now;

    const params: Record<string, string> = {};
    if (label) params.content_name = label;

    trackMetaEvent(name, params);
  };

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}